import { supabase } from '~/supabase';
import type { Employee, Location, Req } from '~/types';
import { useAppStore, useAuthStore } from '#imports';

export const useRequestStore = defineStore('requestStore', () => {
    const { txt } = storeToRefs(useAppStore());
    const { active_emp, isAdmin, isDemo, isManager, profile } = storeToRefs(useAuthStore());
    const _isPending = ref<boolean>(false);
    const pendingReqCnt = ref<number>(0);
    const requests = ref<Array<Req>>([]);
    const requests_cnt = ref<number>(0);
    const requestsForVisualization = ref<Array<any>>([]);
    const statuses = computed( () => [
        { id: 1, name: txt.value.pending },
        { id: 2, name: txt.value.approved },
        { id: 3, name: txt.value.denied }
    ]);
    const _blockFetchRequests = ref<boolean>(false);

    const isPending = computed(() => { return _isPending.value });
    
    const params = ref<any>({
        isAsc: true,
        orderBy: 'start',
        page: 1,
        showArchive: false,
        perPage: Number.MAX_SAFE_INTEGER,
        search: ''
    });

    const startIndex = computed(() => { return (params.value.page - 1) * params.value.perPage; });
    const endIndex = computed(() => { return startIndex.value + params.value.perPage - 1 });

    const blankRequest = computed((): Req => {
        let tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(0, 0, 0, 0);
        const start = new Date(tomorrow);
        const end = new Date(tomorrow);
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 45, 0, 0)
        return {
            id: 0,
            created_by: parseInt(profile.value.account_id),
            created_at: new Date(),
            modified_at: new Date(),
            employee: {} as Employee,
            location: {} as Location,
            status: 1,
            start: start,
            end: end,
            note: ''
        };
    });

    function $reset(): void {
        _isPending.value = false;
        requests.value = [];
        requests_cnt.value = 0;
        params.value = {
            isAsc: true,
            orderBy: 'start',
            page: 1,
            showArchive: false,
            perPage: Number.MAX_SAFE_INTEGER,
            search: ''
        };
    }

    const realtime = supabase
        .channel('request-channel')
        .on(
            'postgres_changes',
            { event: '*', schema: 'public', table: (isDemo.value ? 'request_demo' : 'request') },
            (payload) => {
                console.log('request-channel payload:', payload);
                fetchRequests(active_emp.value.location.id);
            }
        )
        .subscribe();

    async function countPendingRequests(location_id: number): Promise<void> {
        if (!active_emp.value || !active_emp.value.id) {
            pendingReqCnt.value = 0;
            return;
        }

        let query = supabase
            .from(isDemo.value ? 'request_demo' : 'request')
            .select('*')
            .eq('location_id', location_id)
            .eq('status', 1);

        if (!isAdmin.value) {
            query = query.eq('employee_id', active_emp.value.id);
        }

        const { data } = await query;

        pendingReqCnt.value = !!data ? data.length : 0;
    }

    async function fetchRequestsForVisualization(location_id: number): Promise<void> {
        let query =
        isDemo.value
        ? supabase
            .from('request_demo')
            .select('id, employee_demo:employee_id(id, fname, lname), status, start, end, note')
            .eq('location_id', location_id)
        : supabase
            .from('request')
            .select('id, employee:employee_id(id, fname, lname), status, start, end, note')
            .eq('location_id', location_id);

        if (!isManager.value) {
            query = query.eq('employee_id', active_emp.value.id || 0);
        }
        
        const { data, error } = await query;
        
        if (data) {
            requestsForVisualization.value = (<unknown>data as Array<Req>).map(d => {
                return {
                    id: d.id,
                    status: d.status,
                    start: new Date(d.start),
                    end: new Date(d.end),
                    employee: isDemo.value ? { id: d.employee_demo.id } : { id: d.employee.id },
                    name: isDemo.value ? `${d.employee_demo.fname} ${d.employee_demo.lname[0]}.` : `${d.employee.fname} ${d.employee.lname[0]}.`,
                    note: d.note
                }
            });
        }
    }

    async function fetchRequests(location_id: number): Promise<string> {
        console.log('fetchRequests START--------------------------------------------');
        if (_blockFetchRequests.value) return 'Error:  Please try again';
        await countPendingRequests(location_id);
        _isPending.value = true;

        let msg = '';

        let query =
        isDemo.value
        ? supabase
            .from('request_demo')
            .select('id, created_by, created_at, modified_at, employee_demo:employee_id(*), location_demo:location_id(*), status, start, end, note', { count: 'exact', head: false })
            .eq('location_id', location_id)
        : supabase
            .from('request')
            .select('id, created_by, created_at, modified_at, employee:employee_id(*), location:location_id(*), status, start, end, note', { count: 'exact', head: false })
            .eq('location_id', location_id);

        if (!isManager.value) {
            query = query.eq('employee_id', active_emp.value.id || 0);
        }
        
        if (params.value.showArchive) {
            query = query.neq('status', 1);
        }
        else {
            query = query.eq('status', 1);
        }

        console.log('params.value.orderBy', params.value.orderBy);
        query = params.value.orderBy === 'employee.fname'
            ? query.order(isDemo.value ? 'employee_demo(fname)' : 'employee(fname)', { ascending: params.value.isAsc })
            : query.order(params.value.orderBy, { ascending: params.value.isAsc });
        query = query.range(startIndex.value, endIndex.value);
        
        const { data, count, error } = await query;
        
        if (error) {
            msg = error.message as string;
        }
        else if (data && !_blockFetchRequests.value) {
            console.log('fetchRequests.data:', data);
            requests.value = (<unknown>data as Array<Req>).map(d => {
                return {
                    ...d,
                    start: new Date(d.start),
                    end: new Date(d.end),
                    employee: isDemo.value ? d.employee_demo : d.employee,
                    location: isDemo.value ? d.location_demo : d.location
                }
            });
            requests_cnt.value = count || 0;
        }

        _blockFetchRequests.value = false;
        _isPending.value = false;
        console.log('fetchRequests END----------------------------------------------');
        return msg;
    }

    async function fetchRequestsForSchedule(location_id: number, startDate: Date, endDate: Date): Promise<string> {
        console.log('fetchRequestsForSchedule START---------------------------------');
        _isPending.value = true;
        _blockFetchRequests.value = true;

        let msg = '';

        const start = new Date(startDate);
        const end = new Date(endDate);

        const { data, count, error } =
        isDemo.value
        ? await supabase
            .from('request_demo')
            .select('id, created_by, created_at, modified_at, employee_demo:employee_id(*), location_demo:location_id(*), status, start, end, note', { count: 'exact', head: false })
            .eq('location_id', location_id)
            .neq('status', 3)
            .lt('start', end.toISOString())
            .gt('end', start.toISOString())
            .order('employee_demo(fname)', { ascending: true })
            .order('start', { ascending: true })
        : await supabase
            .from('request')
            .select('id, created_by, created_at, modified_at, employee:employee_id(*), location:location_id(*), status, start, end, note', { count: 'exact', head: false })
            .eq('location_id', location_id)
            .neq('status', 3)
            .lt('start', end.toISOString())
            .gt('end', start.toISOString())
            .order('employee(fname)', { ascending: true })
            .order('start', { ascending: true });
        
        if (error) {
            msg = error.message as string;
        }
        else if (data) {
            console.log('fetchRequestsForSchedule.data:', data);
            requests.value = (<unknown>data as Array<Req>).map(d => {
                return {
                    ...d,
                    start: new Date(d.start),
                    end: new Date(d.end),
                    employee: isDemo.value ? d.employee_demo : d.employee,
                    location: isDemo.value ? d.location_demo : d.location
                }
            });
            requests_cnt.value = count || 0;
        }

        _blockFetchRequests.value = false;
        _isPending.value = false;
        console.log('fetchRequestsForSchedule END-----------------------------------');
        return msg;
    }

    async function addUpdateRequest(request: Req): Promise<string> {
        if (_isPending.value) return 'Error:  Please try again';
        _isPending.value = true;

        let msg = '';
        let created_at = null;

        if (!request.id) request.id = 0;

        const { count } = await supabase
            .from(isDemo.value ? 'request_demo' : 'request')
            .select('id, employee_id, start, end', { count: 'exact', head: false })
            .or(`and(start.lte.${new Date(request.start).toISOString()},end.gt.${new Date(request.start).toISOString()},employee_id.eq.${request.employee.id},id.neq.${request.id}),and(start.lt.${new Date(request.end).toISOString()},end.gte.${new Date(request.end).toISOString()},employee_id.eq.${request.employee.id},id.neq.${request.id}),and(start.gte.${new Date(request.start).toISOString()},end.lte.${new Date(request.end).toISOString()},employee_id.eq.${request.employee.id},id.neq.${request.id})`);

        if (count && count > 0) {
            _isPending.value = false;
            return `This request overlaps with an existing request for ${request.employee.fname} ${request.employee.lname}.`;
        }
        
        const { count: count_max_req_per_day } = await supabase
            .from(isDemo.value ? 'request_demo' : 'request')
            .select('id, location_id, start, end', { count: 'exact', head: false })
            .or(`and(start.lte.${new Date(request.start).toISOString()},end.gt.${new Date(request.start).toISOString()},location_id.eq.${request.location.id},id.neq.${request.id}),and(start.lt.${new Date(request.end).toISOString()},end.gte.${new Date(request.end).toISOString()},location_id.eq.${request.location.id},id.neq.${request.id}),and(start.gte.${new Date(request.start).toISOString()},end.lte.${new Date(request.end).toISOString()},location_id.eq.${request.location.id},id.neq.${request.id})`);

        if (count_max_req_per_day && count_max_req_per_day > request.location.max_req_per_day) {
            _isPending.value = false;
            return `This request exceeds per maximum of ${request.location.max_req_per_day} requests allowed on a given day.`;
        }

        if (!isAdmin.value) {
            // Check if request overlaps with any blocked days
            // Convert request start/end to date-only format for comparison with blocked days
            const requestStartDate = new Date(request.start);
            const requestEndDate = new Date(request.end);
            requestStartDate.setHours(0, 0, 0, 0);
            requestEndDate.setHours(0, 0, 0, 0);
            
            const { count: countBlockedDays } = await supabase
                .from(isDemo.value ? 'blocked_day_demo' : 'blocked_day')
                .select('id, date', { count: 'exact', head: false })
                .eq('location_id', request.location.id)
                .gte('date', requestStartDate.toISOString())
                .lte('date', requestEndDate.toISOString());

            if (countBlockedDays && countBlockedDays > 0) {
                _isPending.value = false;
                return `This request overlaps with a blocked day.`;
            }
        }

        // Inserted: fetch overlapping requests at the same location (all employees)
        {
            const startIso = new Date(request.start).toISOString();
            const endIso = new Date(request.end).toISOString();
            const overlapTable = isDemo.value ? 'request_demo' : 'request';

            const { data: overlappingRequests, error: overlapError } = await supabase
                .from(overlapTable)
                .select('id, start, end')
                .eq('location_id', request.location.id)
                .lt('start', endIso)
                .gt('end', startIso)
                .neq('id', request.id || 0);

            if (overlapError) {
                console.warn('Error fetching overlapping requests:', overlapError);
            } else {
                // overlappingRequests is an array of objects { id, start, end }
                // ...use or inspect overlappingRequests as needed...
                console.log('overlappingRequests:', overlappingRequests);

                // Check each calendar day within the current request timespan.
                if (overlappingRequests && overlappingRequests.length > 0) {
                    const reqStart = new Date(request.start);
                    const reqEnd = new Date(request.end);

                    // Normalize iteration start to the beginning of the day of reqStart
                    let day = new Date(reqStart);
                    day.setHours(0, 0, 0, 0);

                    // Iterate each day while the day's start is before the request end
                    while (day < reqEnd) {
                        const dayStart = new Date(day);
                        const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000); // next day 00:00

                        const countForDay = overlappingRequests.filter((r: any) => {
                            const rStart = new Date(r.start);
                            const rEnd = new Date(r.end);
                            // overlap if rStart < dayEnd && rEnd > dayStart
                            return rStart < dayEnd && rEnd > dayStart;
                        }).length;

                        console.log(`Date: ${dayStart.toDateString()}, Overlapping Requests: ${countForDay}`);
                        if (!isAdmin.value && countForDay >= request.location.max_req_per_day) {
                            _isPending.value = false;
                            return 'No more requests are allowed on a date within this request.';
                        }

                        // move to next day
                        day = new Date(day.getTime() + 24 * 60 * 60 * 1000);
                    }
                }
            }
        }
        
        if (request.id > 0) {
            created_at = request.created_at;
            _isPending.value = false;
            await deleteRequest(request.id);
            _isPending.value = true;
        }

        const { data, error } = await supabase
            .from(isDemo.value ? 'request_demo' : 'request')
            .insert([
                {
                    created_by: profile.value.account_id,
                    created_at: new Date(created_at ? created_at : Date.now()).toISOString(),
                    modified_at: new Date().toISOString(),
                    employee_id: request.employee.id,
                    location_id: request.location.id,
                    start: new Date(request.start).toISOString(),
                    end: new Date(request.end).toISOString(),
                    status: request.status,
                    note: request.note
                }
            ])
            .select();

        if (error) {
            msg = error.message as string;
        }
        else if (data) {
            //TODO: if req is approved, remove overlapping shifts
            console.log('data:', data);
            
            /*
            if (request.status === 2) {
                await supabase
                    .from('shift')
                    .delete()
                    .eq('location_id', active_emp.value.location.id)
                    .eq('employee_id', request.employee.id)
                    .neq('status', 0)
                    .or(`and(start.lte.${new Date(request.start).toISOString()},end.gt.${new Date(request.start).toISOString()}),and(start.lt.${new Date(request.end).toISOString()},end.gte.${new Date(request.end).toISOString()}),and(start.gte.${new Date(request.start).toISOString()},end.lte.${new Date(request.end).toISOString()})`);
            }
                    */
        }

        _isPending.value = false;
        return msg;
    }

    /**
     * Set the status of a request.
     * 
     * @param {Req} request 
     * @param {number} status The status code [pending: 1, approved: 2, denied: 3]
     */
    async function setStatus(request: Req, status: number): Promise<string> {
        if (_isPending.value) return 'Error:  Please try again';

        status = Math.floor(status);
        if (status < 1 || status > 3) return 'Invalid status.';

        request.status = status;

        return await addUpdateRequest(request);
    }

    async function deleteRequest(id: number) {
        if (_isPending.value) return 'Error:  Please try again';
        _isPending.value = true;

        let msg = '';

        const { error } = await supabase
            .from(isDemo.value ? 'request_demo' : 'request')
            .delete()
            .eq('id', id);

        if (error) {
            msg = error.message as string;
        }

        _isPending.value = false;
        return msg;
    }

    return {
        blankRequest,
        requests,
        requests_cnt,
        requestsForVisualization,
        isPending,
        pendingReqCnt,
        params,
        statuses,
        addUpdateRequest,
        deleteRequest,
        fetchRequests,
        fetchRequestsForSchedule,
        fetchRequestsForVisualization,
        setStatus,
        $reset
    }
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useRequestStore, import.meta.hot))
}